import { encrypt, decrypt, isSensitiveField } from './crypto';
import { requestApp, WEB_TO_APP } from '../webview';

// 스토리지 키
const STORAGE_KEYS = {
    USER_INFO: 'dokocrew_user_info',
    ACCESS_TOKEN: 'accessToken',
    REFRESH_TOKEN: 'refreshToken'
};

/**
 * 사용자 정보 인터페이스 (wiz-sso UserResponse 기반)
 */
export interface UserInfo {
    userId: string;          // 암호화 대상 (SSO에서 이미 암호화되어 옴)
    userNo?: number;         // 사용자 번호 (wiz-sso user 테이블 PK)
    nickname: string;        // 평문 저장 (표시용)
    ctn?: string;            // 본인 연락처 - 암호화 대상
    age?: string;            // 생년월일/나이 - 암호화 대상
    profileImgPath?: string; // 프로필 이미지 경로 - 평문
    profileImgNo?: string;   // 프로필 이미지 번호 - 평문
    statusMessage?: string;  // 상태 메시지 - 평문
    isMinor?: string;        // 미성년자 여부 - 평문
    guardCtn?: string;       // 보호자 연락처 - 암호화 대상
    locale?: string;         // 언어 설정 - 평문
    distanceUnit?: string;   // 거리 단위 - 평문
}

/**
 * 암호화된 사용자 정보 인터페이스 (저장용)
 */
interface EncryptedUserInfo {
    userId: string;
    userNo?: number;
    nickname: string;
    ctn?: string;
    age?: string;
    profileImgPath?: string;
    profileImgNo?: string;
    isMinor?: string;
    guardCtn?: string;
    locale?: string;
    distanceUnit?: string;
    _encrypted: boolean;  // 암호화 여부 플래그
}

/**
 * 민감정보를 암호화하여 저장
 * userId는 SSO에서 이미 암호화되어 오므로 그대로 저장
 * ctn, age, guardCtn은 추가로 암호화
 */
const encryptUserInfo = (userInfo: UserInfo): EncryptedUserInfo => {
    return {
        userId: userInfo.userId, // SSO에서 이미 암호화됨
        userNo: userInfo.userNo,
        nickname: userInfo.nickname, // 닉네임은 평문
        ctn: userInfo.ctn ? encrypt(userInfo.ctn) : undefined,
        age: userInfo.age ? encrypt(userInfo.age) : undefined,
        profileImgPath: userInfo.profileImgPath,
        profileImgNo: userInfo.profileImgNo,
        isMinor: userInfo.isMinor,
        guardCtn: userInfo.guardCtn ? encrypt(userInfo.guardCtn) : undefined,
        locale: userInfo.locale,
        distanceUnit: userInfo.distanceUnit,
        _encrypted: true
    };
};

/**
 * 암호화된 정보를 복호화
 */
const decryptUserInfo = (encryptedInfo: EncryptedUserInfo): UserInfo => {
    if (!encryptedInfo._encrypted) {
        // 암호화되지 않은 데이터
        const { _encrypted, ...rest } = encryptedInfo;
        return rest as UserInfo;
    }

    return {
        userId: encryptedInfo.userId, // SSO 암호화 상태 유지
        userNo: encryptedInfo.userNo,
        nickname: encryptedInfo.nickname,
        ctn: encryptedInfo.ctn ? decrypt(encryptedInfo.ctn) : undefined,
        age: encryptedInfo.age ? decrypt(encryptedInfo.age) : undefined,
        profileImgPath: encryptedInfo.profileImgPath,
        profileImgNo: encryptedInfo.profileImgNo,
        isMinor: encryptedInfo.isMinor,
        guardCtn: encryptedInfo.guardCtn ? decrypt(encryptedInfo.guardCtn) : undefined,
        locale: encryptedInfo.locale,
        distanceUnit: encryptedInfo.distanceUnit
    };
};

/**
 * 사용자 정보 저장 (민감정보 암호화)
 */
export const saveUserInfo = (userInfo: UserInfo): void => {
    try {
        const encryptedInfo = encryptUserInfo(userInfo);
        const jsonString = JSON.stringify(encryptedInfo);

        // sessionStorage에 저장 (브라우저 탭 닫히면 삭제)
        sessionStorage.setItem(STORAGE_KEYS.USER_INFO, jsonString);

        // 닉네임은 별도로 평문 저장 (빠른 접근용)
        sessionStorage.setItem('userName', userInfo.nickname);

        // 웹뷰 앱에 사용자 정보 전송 (앱 네이티브 저장소 활용)
        requestApp(WEB_TO_APP.SAVE_USER_INFO, encryptedInfo);
    } catch (error) {
        console.error('Error saving user info:', error);
    }
};

/**
 * 사용자 정보 조회 (복호화하여 반환)
 */
export const getUserInfo = (): UserInfo | null => {
    try {
        const jsonString = sessionStorage.getItem(STORAGE_KEYS.USER_INFO);
        if (!jsonString) return null;

        const encryptedInfo: EncryptedUserInfo = JSON.parse(jsonString);
        const userInfo = decryptUserInfo(encryptedInfo);

        // 복호화 실패로 핵심 필드가 비어있으면 손상된 데이터 제거
        if (!userInfo.userId && !userInfo.nickname) {
            console.warn('Corrupted user info detected, clearing storage');
            sessionStorage.removeItem(STORAGE_KEYS.USER_INFO);
            return null;
        }

        return userInfo;
    } catch (error) {
        console.error('Error getting user info, clearing corrupted data:', error);
        sessionStorage.removeItem(STORAGE_KEYS.USER_INFO);
        return null;
    }
};

/**
 * 닉네임만 빠르게 조회 (암호화되지 않음)
 */
export const getNickname = (): string => {
    return sessionStorage.getItem('userName') || '';
};

/**
 * 사용자 정보 삭제
 */
export const clearUserInfo = (): void => {
    sessionStorage.removeItem(STORAGE_KEYS.USER_INFO);
    sessionStorage.removeItem('userName');
    sessionStorage.removeItem('userId');
    sessionStorage.removeItem('userNo');

    // 웹뷰 앱에도 삭제 요청
    requestApp(WEB_TO_APP.CLEAR_USER_INFO, {});
};

/**
 * 토큰 저장
 */
export const saveTokens = (accessToken: string, refreshToken?: string): void => {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    if (refreshToken) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    }

    // 웹뷰 앱에 토큰 전송
    requestApp(WEB_TO_APP.SEND_TOKEN, {
        access_token: accessToken,
        refresh_token: refreshToken
    });
};

/**
 * 액세스 토큰 조회
 */
export const getAccessToken = (): string | null => {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
};

/**
 * 리프레시 토큰 조회
 */
export const getRefreshToken = (): string | null => {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
};

/**
 * 토큰 삭제 — 웹 localStorage + 네이티브 MMKV 동시 정리
 */
export const clearTokens = (): void => {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    // 네이티브 MMKV 토큰 + 위치추적 정리 — 미연결 시 LBS 401 폭주 원인이 됨
    requestApp(WEB_TO_APP.CLEAR_AUTH, {});
};

/**
 * 모든 인증 정보 삭제 (로그아웃)
 */
export const clearAll = (): void => {
    clearUserInfo();
    clearTokens();
};
